import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'emergency';
  category: 'governance' | 'finance' | 'maintenance' | 'social' | 'marketplace' | 'system';
  isRead: boolean;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, unknown>;
  expiresAt?: string;
  createdAt: string;
}

interface AppState {
  // Theme and UI
  theme: 'light' | 'dark' | 'system';
  sidebarCollapsed: boolean;

  // Notifications
  notifications: Notification[];
  unreadCount: number;

  // Global loading states
  isLoading: boolean;
  loadingMessage?: string;

  // Module configuration (from tenant settings)
  enabledModules: string[];

  // AI Assistant
  aiAssistantOpen: boolean;
  aiContext: Record<string, unknown>;

  // Actions
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Notification actions
  addNotification: (notification: Omit<Notification, 'id' | 'isRead' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Loading actions
  setLoading: (loading: boolean, message?: string) => void;

  // Module actions
  setEnabledModules: (modules: string[]) => void;
  isModuleEnabled: (module: string) => boolean;

  // AI Assistant actions
  toggleAiAssistant: () => void;
  setAiContext: (context: Record<string, unknown>) => void;
}

const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // Initial state
      theme: 'system',
      sidebarCollapsed: false,
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      loadingMessage: undefined,
      enabledModules: [
        'governance',
        'finance',
        'marketplace',
        'maintenance',
        'social',
        'communication',
        'documents',
      ],
      aiAssistantOpen: false,
      aiContext: {},

      // Theme and UI actions
      setTheme: (theme) => {
        set({ theme });

        // Apply theme to document
        if (theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else if (theme === 'light') {
          document.documentElement.classList.remove('dark');
        } else {
          // System theme
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          if (prefersDark) {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        }
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed) => {
        set({ sidebarCollapsed: collapsed });
      },

      // Notification actions
      addNotification: (notificationData) => {
        const notification: Notification = {
          ...notificationData,
          id: generateId(),
          isRead: false,
          createdAt: new Date().toISOString(),
        };

        set((state) => ({
          notifications: [notification, ...state.notifications],
          unreadCount: state.unreadCount + 1,
        }));
      },

      markAsRead: (id) => {
        set((state) => {
          const notifications = state.notifications.map((n) =>
            n.id === id ? { ...n, isRead: true } : n,
          );
          const unreadCount = notifications.filter((n) => !n.isRead).length;

          return { notifications, unreadCount };
        });
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
          unreadCount: 0,
        }));
      },

      removeNotification: (id) => {
        set((state) => {
          const notifications = state.notifications.filter((n) => n.id !== id);
          const unreadCount = notifications.filter((n) => !n.isRead).length;

          return { notifications, unreadCount };
        });
      },

      clearNotifications: () => {
        set({ notifications: [], unreadCount: 0 });
      },

      // Loading actions
      setLoading: (loading, message) => {
        set({ isLoading: loading, loadingMessage: message });
      },

      // Module actions
      setEnabledModules: (modules) => {
        set({ enabledModules: modules });
      },

      isModuleEnabled: (module) => {
        const { enabledModules } = get();
        return enabledModules.includes(module);
      },

      // AI Assistant actions
      toggleAiAssistant: () => {
        set((state) => ({ aiAssistantOpen: !state.aiAssistantOpen }));
      },

      setAiContext: (context) => {
        set({ aiContext: context });
      },
    }),
    { name: 'AppStore' },
  ),
);
